import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { pointsCreator } from "../Sites/SiteAboutPage/SiteAboutPageUtils/pointsCreator";
import { resizeImage } from "../Sites/SiteAboutPage/SiteAboutPageUtils/resizeImage";
import { useCameraPreviewUrl } from "./EditCameraUtils/useCameraPreviewUrl";
import { HistoryCoordinates } from "./HistoryCoordinates";

const ACTUATE_ORANGE = "#FF8400";

export const IgnoreZonesPreviewDialog = ({
  open,
  onClose,
  cameraId,
  polygonalZones,
}: {
  open: boolean;
  onClose: () => void;
  cameraId: string;
  polygonalZones: number[][][] | null;
}) => {
  const { previewUrl, loading, error } = useCameraPreviewUrl(open, cameraId);
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  React.useEffect(() => {
    if (open) setDimensions(null);
  }, [open, cameraId]);

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setDimensions(resizeImage(naturalWidth, naturalHeight));
  };

  const hasZones =
    Array.isArray(polygonalZones) && polygonalZones.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { fontFamily: "Mulish" },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <span>Ignore Zones</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: "text.primary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography
            sx={{ fontFamily: "Mulish", color: "error.main", py: 2 }}
          >
            {error}
          </Typography>
        )}
        {!loading && !error && previewUrl && (
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <img
              src={previewUrl}
              onLoad={handleImageLoad}
              alt="Camera preview"
              style={{
                width: dimensions ? `${dimensions.width}px` : "auto",
                height: dimensions ? `${dimensions.height}px` : "auto",
                display: "block",
              }}
            />
            {dimensions && hasZones &&
              polygonalZones!.map((polygon, polygonIndex) => (
                <div
                  key={polygonIndex}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    pointerEvents: "none",
                  }}
                >
                  <svg
                    style={{
                      width: `${dimensions.width}px`,
                      height: `${dimensions.height}px`,
                    }}
                  >
                    <polygon
                      points={pointsCreator(
                        polygon,
                        dimensions.height,
                        dimensions.width
                      )}
                      stroke={ACTUATE_ORANGE}
                      strokeWidth="5"
                      fill={ACTUATE_ORANGE}
                      fillOpacity="0.5"
                    />
                  </svg>
                </div>
              ))}
            {dimensions && !hasZones && (
              <Typography
              >
                There were no ignore zones assigned for this product during this change.
              </Typography>
            )}
          </Box>
        )}
        {!loading && hasZones && <HistoryCoordinates value={polygonalZones} />}
      </DialogContent>
    </Dialog>
  );
};
